import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api, setAuthToken, setUnauthorizedHandler } from '../../lib/api';
import type { AuthUser, LoginResult } from '../../types/api';

/** localStorage key holding the access token. */
const TOKEN_STORAGE_KEY = 'rfp.auth.token';

interface AuthContextValue {
  user: AuthUser | null;
  /** True until a stored token has been checked against `GET /auth/me`. */
  initializing: boolean;
  signIn: (email: string, password: string) => Promise<AuthUser>;
  signOut: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Holds the signed-in session.
 *
 * The JWT is kept in localStorage so a reload does not sign the user out; it is
 * re-validated against `GET /auth/me` on boot, which also picks up an account
 * that was deactivated or deleted since the token was issued.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() =>
    window.localStorage.getItem(TOKEN_STORAGE_KEY),
  );
  const [user, setUser] = useState<AuthUser | null>(null);
  const [initializing, setInitializing] = useState(true);

  const clearSession = useCallback(() => {
    window.localStorage.removeItem(TOKEN_STORAGE_KEY);
    setAuthToken(null);
    setToken(null);
    setUser(null);
  }, []);

  // Any 401 from any endpoint means the token is no longer usable.
  useEffect(() => {
    setUnauthorizedHandler(clearSession);
    return () => setUnauthorizedHandler(null);
  }, [clearSession]);

  useEffect(() => {
    if (!token) {
      setUser(null);
      setInitializing(false);
      return;
    }

    // `setAuthToken` has to happen before the request so the header is attached.
    setAuthToken(token);
    let active = true;
    setInitializing(true);

    api
      .get<{ user: AuthUser }>('/auth/me')
      .then((result) => {
        if (active) setUser(result.user);
      })
      .catch(() => {
        if (active) clearSession();
      })
      .finally(() => {
        if (active) setInitializing(false);
      });

    return () => {
      active = false;
    };
  }, [token, clearSession]);

  const signIn = useCallback(async (email: string, password: string) => {
    const result = await api.post<LoginResult>('/auth/login', { email, password });
    window.localStorage.setItem(TOKEN_STORAGE_KEY, result.token);
    // Set the token before updating state so nothing races the first request.
    setAuthToken(result.token);
    setToken(result.token);
    setUser(result.user);
    setInitializing(false);
    return result.user;
  }, []);

  const signOut = useCallback(() => {
    // Tokens are stateless: this call is only an audit hook, dropping it is the
    // actual logout, so a failure here must not block the user.
    void api.post('/auth/logout').catch(() => undefined);
    clearSession();
  }, [clearSession]);

  const value = useMemo<AuthContextValue>(
    () => ({ user, initializing, signIn, signOut }),
    [user, initializing, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside an <AuthProvider>');
  return context;
}
